import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useFeedback } from './FeedbackContext';
import api from '../api/api';

interface User {
  id: number;
  username: string;
  role: string;
  school_id?: number;
  email: string;
  full_name?: string;
  is_active?: boolean;
  created_at?: string;
  last_login?: string;
  school_name?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (user: User) => void;
  logout: () => void;
  logoutWithMessage: (msg: string) => void;
  refreshToken: () => Promise<boolean>;
  isRefreshing: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Utility to check if token is expired or will expire soon (within 5 minutes)
function isTokenExpiredOrExpiringSoon(token: string): boolean {
  try {
    const decoded: any = JSON.parse(atob(token.split('.')[1]));
    if (!decoded.exp) return false;
    // Check if token expires within the next 5 minutes
    const fiveMinutesFromNow = Date.now() + (5 * 60 * 1000);
    return decoded.exp * 1000 <= fiveMinutesFromNow;
  } catch {
    return true;
  }
}

// Utility to check if token is completely expired
function isTokenExpired(token: string): boolean {
  try {
    const decoded: any = JSON.parse(atob(token.split('.')[1]));
    if (!decoded.exp) return false;
    return Date.now() >= decoded.exp * 1000;
  } catch {
    return true;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const feedback = useFeedback();

  const refreshToken = useCallback(async (): Promise<boolean> => {
    console.log('🔄 Starting token refresh process...');
    
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      console.log('❌ No refresh token found in localStorage');
      return false;
    }

    console.log('📡 Refresh token found, making API request...');
    setIsRefreshing(true);
    
    try {
      console.log('🌐 Sending refresh request to /refresh endpoint...');
      const response = await api.post('/refresh', null, {
        params: { refresh_token: refreshToken }
      });
      
      console.log('✅ Refresh request successful');
      const { access_token, refresh_token: newRefreshToken, user: userData } = response.data as {
        access_token: string;
        refresh_token?: string;
        user?: User;
      };
      
      console.log('📦 Processing response data:', {
        hasAccessToken: !!access_token,
        hasRefreshToken: !!newRefreshToken,
        hasUser: !!userData,
        userRole: userData?.role
      });
      
      localStorage.setItem('token', access_token);
      console.log('💾 New access token stored');
      
      if (newRefreshToken) {
        localStorage.setItem('refresh_token', newRefreshToken);
        console.log('💾 New refresh token stored');
      }
      
      if (userData) {
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        console.log('👤 User data updated');
      }
      
      setIsAuthenticated(true);
      console.log('✅ Token refresh completed successfully');
      return true;
    } catch (error: any) {
      console.error('❌ Token refresh failed:', error);
      console.error('Error details:', {
        message: error?.message || 'Unknown error',
        status: error?.response?.status,
        data: error?.response?.data
      });
      return false;
    } finally {
      setIsRefreshing(false);
      console.log('🔄 Token refresh process finished');
    }
  }, []);

  const login = (user: User) => {
    setIsAuthenticated(true);
    setUser(user);
    localStorage.setItem('user', JSON.stringify(user));
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
  };

  const logoutWithMessage = (msg: string) => {
    logout();
    feedback.showInfo(msg);
  };

  // Set up automatic token refresh
  useEffect(() => {
    const checkAndRefreshToken = async () => {
      console.log('🔍 Checking token expiration...');
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('⚠️ No access token found');
        return;
      }

      const isExpiringSoon = isTokenExpiredOrExpiringSoon(token);
      console.log(`🔍 Token expiration check: ${isExpiringSoon ? 'Expiring soon' : 'Valid'}`);
      
      if (isExpiringSoon) {
        console.log('🔄 Token expiring soon, initiating refresh...');
        const success = await refreshToken();
        if (!success) {
          console.log('❌ Token refresh failed, logging out user');
          logoutWithMessage('Session expired, please log in again.');
        } else {
          console.log('✅ Token refresh successful, user session maintained');
        }
      }
    };

    // Check token on mount
    console.log('🚀 Initial token check on app mount');
    checkAndRefreshToken();

    // Set up interval to check token every minute
    console.log('⏰ Setting up automatic token refresh (every 60 seconds)');
    const interval = setInterval(checkAndRefreshToken, 60 * 1000);

    return () => {
      console.log('🧹 Cleaning up token refresh interval');
      clearInterval(interval);
    };
  }, [refreshToken, logoutWithMessage]);

  // Handle session expired events
  useEffect(() => {
    const handleSessionExpired = async () => {
      // Try to refresh token first
      const success = await refreshToken();
      if (!success) {
        logoutWithMessage('Session expired, please log in again.');
      }
    };

    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, [refreshToken, logoutWithMessage]);

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      login, 
      logout, 
      logoutWithMessage, 
      refreshToken,
      isRefreshing 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}; 